import { combineLatest, debounceTime, Observable, of, Subscription, throwError, timer } from 'rxjs';
import { LoadingService, PageLoadingState } from '../services/loading.service';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { DataUtils } from '@/core/utils/data-utils';
import { CloneUtils } from '@/core/utils/clone-utils';

export type ChangeBehavior = 'always' | 'whenChanged' | 'onlyCallback';
/**
 * ErrorBehavior for handling errors in observables.
 * - lastValueDie (DEFAULT): Emits the last value and completes the observables, so that it won't emit again.
 * - die: Throws the error, which will make the observable fail and die.
 * - recover: Recovers the observable by returning the caught observable.
 */
export type ErrorBehavior = 'die' | 'lastValueDie' | 'recover';

/**
 * The ValueMode determines how emitted dependency values are treated for change detection.
 *
 * - immutable:
 *   Use this mode when emitted values are immutable. In this case, a value is never mutated
 *   after emission. Any logical change must therefore be emitted as a new value.
 *   This allows the InitializationManager to avoid defensive cloning and use a more efficient
 *   change detection strategy.
 *
 * - mutable:
 *   Use this mode when emitted values may be mutated after emission. In this case, the same
 *   object reference may later contain different field values.
 *   To detect such changes reliably, the InitializationManager stores a cloned snapshot and
 *   performs deep value comparison.
 *   This is the safer but slower strategy.
 *
 * "mutable" is the default mode.
 */
export type ValueMode = 'immutable' | 'mutable';

export type DependencyOpts<T> = {
    name?: string; // optional, for logging
    callback?: (value: T) => void;
    changeBehavior?: ChangeBehavior;
    errorBehavior?: ErrorBehavior;
    valueMode?: ValueMode;
};

type Dependency<T> = {
    observable: Observable<T>;
    name: string;
    lastValue: T | undefined;
    callback: ((value: T) => void) | undefined;
    changeBehavior: ChangeBehavior;
    errorBehavior: ErrorBehavior;
    valueMode: ValueMode;
};

type InitializationStatus = 'none' | 'initializing' | 'dependencies-loaded' | 'data-loaded';

/**
 * @class InitializationManager
 * @description The InitializationManager is used to help with the data initialization of components.
 *  It standardizes the flow and load behavior.
 *  It is designed to bring reactivity easily to component design / behavior.
 *  It works as follows:
 *   - a component depends on data to function correctly
 *   - data like the current user, translations, etc. normally do not change while the component is loaded, still the component should react to changes correctly;
 *     as the component depends on this data but that is not mainly handling it (e.g., the current user) this kind of data is defined as a dependency in the sense of this class
 *   - further data, the real data that the component is working with, is handled separately inside the component, typically loaded in onInit(), onChange() and by the components other functions as needed
 *   - for example,
 *     - the dashboard needs translations, the current user, layers, tiles, etc. but the real data to be displayed is the chart
 *     - you then define all necessary base data as dependencies, e.g. currentUser$. This data should come from cached observables.
 *     - in the onInit() and onChange() function, you load / update the chart data, which is displayed to the user
 * The InitializationManager assures that the main data loading is only happening when all necessary dependencies are resolved.
 *
 * @example
 * // preferably instantiate in the components constructor:
 * this.initMgr = new InitializationManager(this.loadingService, this.constructor.name);
 * // in ngOnInit() register dependencies and call initialize()
 * this.initMgr
 *     .addDependency(this.userService.currentUser$, {
 *         name: 'currentUser',
 *         callback: (currentUser: CurrentUser) => {
 *             // called when the observable emits a new changed value
 *             // it will not be called when the observable emits the same value again
 *          }
 *     })
 *     .addDependency(this.reportSettingsService.currentReportSettings$)
 *     .onInit(async ([currentUser, reportSettings]) => {
 *         // do something (with the dependencies) when all have emitted a first time
 *     })
 *     .onChange(async ([currentUser, reportSettings]) => {
 *         // do something (with the dependencies) when a dependency emits a new value
 *         // if this method is called or not depends on the changeBehavior option of the dependency
 *         // it will be called when the behavior is set to 'always' or 'whenChanged', but not when it is set to 'onlyCallback'
 *     })
 *     .initialize();
 * // please note that the typing in onInit and onChange will only work correctly when you chain the building as described above,
 * // otherwise TypeScript will not know what type the elements in the array are and how long it is
 * // do not forget to destroy the manager when the component is destroyed; in ngOnDestroy() call
 * this.initMgr.destroy();
 */
export class InitializationManager<TDeps extends readonly unknown[] = []> {
    // Debounce time (in milliseconds) to avoid multiple triggers from dependent observables.
    // For example, UserService.currentUser$ and ReportSettingsService.currentReportSettings$ (second depends on the first)
    public static readonly debounceTimeMs = 30;
    // recover delay defines how long to wait after an error for streams that have "recover" errorBehavior set and are self-healing
    // consider making recoverDelay a dependency option
    public static readonly recoverDelay = 1000;
    private dependencies: Dependency<unknown>[] = [];
    private onInitFn?: (dependencies: Readonly<TDeps>) => Promise<void>;
    private onChangeFn?: (dependencies: Readonly<TDeps>) => Promise<void>;
    private changeCount = 0;
    private isFirstChange = true;
    private subscriptions = new Subscription();
    private status: InitializationStatus = 'none';
    private debugMode = false;
    private loadingAnim = true;

    constructor(
        private readonly loadingService: LoadingService,
        private readonly component: string,
    ) {}

    /**
     * Sets the loading animation to be shown when the initialization or data loading is in progress.
     * Default is true.
     */
    public loadingAnimation(anim: boolean): this {
        this.loadingAnim = anim;
        return this;
    }

    /**
     * Enables or disables debug logging.
     * @param debug true to enable debug logging, false to disable.
     */
    public setDebug(debug: boolean): this {
        this.debugMode = debug;
        return this;
    }

    /**
     * Destroys the InitializationManager.
     * This method should be called when the component is destroyed.
     * It unsubscribes from all observables.
     */
    public destroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Starts the initialization process.
     * This method should be called after registering all dependencies and hooks.
     * Attention: This method can only be called once.
     */
    public initialize(): void {
        this.logDebug(`initialize() called`);
        if (this.dependencies.length === 0) {
            throw new Error(
                'InitializationManager: No dependencies registered. You need to add dependencies before calling initialize().',
            );
        }
        if (this.status !== 'none') {
            throw new Error('InitializationManager: initialize() can only be called once.');
        }
        this.setLoadingState('initializing');
        this.status = 'initializing';

        const observables: Observable<unknown>[] = [];
        for (const dependency of this.dependencies) {
            dependency.observable = this.addChangeBehavior(dependency);
            observables.push(dependency.observable);
        }

        this.subscriptions.add(
            combineLatest(observables)
                .pipe(debounceTime(InitializationManager.debounceTimeMs))
                .subscribe({
                    next: async (values) => {
                        this.logDebug(`combineLatest subscription triggered, changeCount=${this.changeCount}`);
                        if (this.changeCount > 0) {
                            const typedValues = values as unknown as Readonly<TDeps>;
                            this.changeCount = 0;
                            if (this.isFirstChange) {
                                this.status = 'dependencies-loaded';
                                this.isFirstChange = false;
                                if (this.onInitFn) {
                                    this.setLoadingState('data-loading');
                                    try {
                                        await this.onInitFn(typedValues);
                                        this.status = 'data-loaded';
                                    } catch (error) {
                                        this.logError(`Error in onInit function`, error);
                                    }
                                }
                                this.setLoadingState('complete');
                            } else {
                                if (this.onChangeFn) {
                                    this.setLoadingState('data-loading');
                                    try {
                                        await this.onChangeFn(typedValues);
                                    } catch (error) {
                                        this.logError(`Error in onChange function`, error);
                                    }

                                    this.setLoadingState('complete');
                                }
                            }
                        }
                    },
                    error: (error) => {
                        this.logError(`combineLatest subscription error`, error);
                    },
                }),
        );
    }

    /**
     * Returns true if the initialization of dependencies is complete.
     * This means that all dependency observables have emitted at least once.
     * @returns true if the dependencies are loaded, false otherwise.
     */
    public dependenciesLoaded(): boolean {
        return this.status === 'dependencies-loaded' || this.status === 'data-loaded';
    }

    /**
     * Returns true if the initialization is complete.
     * This means that all dependency observables have emitted at least once and the onInit operation was executed (data loaded).
     * @returns true if the initialization is complete, false otherwise.
     */
    public initializationCompleted(): boolean {
        return this.status === 'data-loaded';
    }

    /**
     * Adds a dependency to the initialization process.
     * When a component needs data, it should depend on an observable from a service.
     * This observable should be added here.
     * For example, UserService.currentUser$
     * @param observable$ The observable that triggers the initialization.
     * @param opts Optional options object for the dependency.
     * @param opts.name Optional name for the dependency. Defaults "Observable_{insert number}".
     * @param opts.callback Optional callback function that is invoked when the observable emits a new value.
     * @param opts.changeBehavior Optional change behavior for the dependency. Defaults to 'whenChanged'.
     * 1. 'always': The callback function and onChange function are always invoked when the observable emits a new value.
     * 2. 'whenChanged': The callback function and onChange functions are only invoked when the observable emits a new changed value (value, not reference comparison).
     * 3. 'onlyCallback': The callback function is always invoked when the observable emits a new changed value, but the onChange function is not invoked.
     */
    public addDependency<T>(
        observable$: Observable<T>,
        opts?: DependencyOpts<T>,
    ): InitializationManager<[...TDeps, T]> {
        const countName = 'Observable_' + (this.dependencies.length + 1);
        this.dependencies.push({
            observable: observable$,
            callback: opts?.callback ?? undefined,
            changeBehavior: opts?.changeBehavior ?? 'whenChanged',
            errorBehavior: opts?.errorBehavior ?? 'lastValueDie',
            valueMode: opts?.valueMode ?? 'mutable',
            name: opts?.name ?? countName,
        } as Dependency<unknown>);
        return this as unknown as InitializationManager<[...TDeps, T]>;
    }

    /**
     * Sets a callback function which is invoked when the initialization is complete.
     * It provides the result of all observables as an array, like combineLatest does.
     * The arrays data is in the order of the registered dependencies.
     * @param fn The callback function that is invoked when the initialization is complete.
     */
    public onInit(fn: (dependencies: Readonly<TDeps>) => Promise<void>): this {
        this.onInitFn = fn;
        return this;
    }

    /**
     * Sets a callback function that is invoked when a dependency emits a new (changed) value.
     * It provides the result of all observables as an array, like combineLatest does.
     * The arrays data is in the order of the registered dependencies.
     * It is not invoked the first time when the initialization is complete. See onInit() for that.
     * @param fn The callback function that is invoked when a change is detected.
     */
    public onChange(fn: (dependencies: Readonly<TDeps>) => Promise<void>): this {
        this.onChangeFn = fn;
        return this;
    }

    private addChangeBehavior(dependency: Dependency<unknown>): Observable<unknown> {
        return dependency.observable.pipe(
            catchError((err, caught$) => {
                this.logError(`Observable "${dependency.name}" failed`, err);
                // TODO consider displaying an error toast here with a message like "internal error, please reload in a few minutes."
                if (dependency.errorBehavior === 'die') {
                    // rethrows the error, which will make the observable run into an error state and die.
                    return throwError(() => err);
                } else if (dependency.errorBehavior === 'lastValueDie') {
                    // emits the last value, but the stream is dead and will not emit any more
                    // suitable for http requests
                    return of(dependency.lastValue);
                } else if (dependency.errorBehavior === 'recover') {
                    // returns the caught observable, so that the stream is not dead, but the error is ignored
                    // suitable for cached observables with auto reload, refresh and auto healing (TtlCacheStore)
                    return timer(InitializationManager.recoverDelay).pipe(switchMap(() => caught$));
                } else {
                    console.error(`Unknown error behavior: ${dependency.errorBehavior}`);
                    return of(dependency.lastValue);
                }
            }),
            tap((value) => {
                this.logDebug(`observable "${dependency.name}" emitted`);
                if (dependency.changeBehavior === 'always') {
                    this.changeCount++;
                } else {
                    if (DataUtils.isEqual(value, dependency.lastValue)) {
                        this.logDebug(`observable "${dependency.name}" unchanged, skipping callback and onChange`);
                        return;
                    }
                    if (dependency.changeBehavior !== 'onlyCallback') {
                        this.changeCount++;
                    }
                }
                if (dependency.valueMode === 'mutable') {
                    // note: When the object reference may stay the same and the object itself is mutated,
                    // the object needs to be cloned to make sure that the equality check works correctly.
                    dependency.lastValue = CloneUtils.cloneData(value);
                } else {
                    dependency.lastValue = value;
                }

                if (dependency.callback) {
                    try {
                        dependency.callback(value);
                    } catch (error) {
                        this.logError(`Error in callback function for ${dependency.name}`, error);
                    }
                }
            }),
        );
    }

    private logDebug(message: string, ...args: unknown[]): void {
        if (this.debugMode) {
            console.debug(`InitializationManager(${this.component}): ${message}`, ...args);
        }
    }

    private logError(message: string, ...args: unknown[]): void {
        console.error(`InitializationManager(${this.component}): ${message}`, ...args);
    }

    private setLoadingState(state: PageLoadingState): void {
        if (this.loadingAnim) {
            this.loadingService.setLoadingState(state, this.component);
        }
    }
}
